/* custom-select.js — replaces native <select> UI with a themed dropdown.
   Progressive enhancement: the native select stays in the DOM (forms, no-JS,
   and change events keep working). On touch devices the native picker is kept
   (better UX) — the closed control is styled via CSS either way. */
(function () {
  'use strict';

  if (window.matchMedia('(pointer: coarse)').matches) return;

  const CHEVRON =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
  const CHECK =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 13 4 4L19 7"/></svg>';

  let openInstance = null;

  function enhance(select) {
    if (select.__cselect) return;
    select.__cselect = true;

    const wrap = document.createElement('div');
    wrap.className = 'cselect';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cselect__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    if (select.id) btn.setAttribute('aria-labelledby', `${select.id}-label ${select.id}-value`);

    const label = document.createElement('span');
    label.className = 'cselect__label';
    if (select.id) label.id = `${select.id}-value`;
    btn.appendChild(label);
    btn.insertAdjacentHTML('beforeend', CHEVRON);

    const panel = document.createElement('ul');
    panel.className = 'cselect__panel card';
    panel.setAttribute('role', 'listbox');
    panel.hidden = true;

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(btn);
    wrap.appendChild(panel);
    wrap.appendChild(select);
    select.classList.add('cselect__native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    function buildOptions() {
      panel.innerHTML = '';
      Array.from(select.options).forEach((opt) => {
        const li = document.createElement('li');
        li.className = 'cselect__option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '-1');
        li.dataset.value = opt.value;
        li.setAttribute('aria-selected', String(opt.selected));
        li.innerHTML = `<span>${opt.textContent}</span>${opt.selected ? CHECK : ''}`;
        panel.appendChild(li);
      });
      label.textContent = select.options[select.selectedIndex] ? select.options[select.selectedIndex].textContent : '';
    }
    buildOptions();

    function close(focusBtn) {
      panel.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      if (openInstance === close) openInstance = null;
      if (focusBtn) btn.focus();
    }

    function open() {
      if (openInstance) openInstance(false);
      buildOptions();
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      openInstance = close;
      const selected = panel.querySelector('[aria-selected="true"]') || panel.firstElementChild;
      if (selected) selected.focus();
    }

    function choose(li) {
      select.value = li.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      buildOptions();
      close(true);
    }

    btn.addEventListener('click', () => (panel.hidden ? open() : close(true)));
    btn.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        open();
      }
    });

    panel.addEventListener('click', (e) => {
      const li = e.target.closest('.cselect__option');
      if (li) choose(li);
    });
    panel.addEventListener('keydown', (e) => {
      const items = Array.from(panel.children);
      const idx = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        (items[idx + 1] || items[0]).focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        (items[idx - 1] || items[items.length - 1]).focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        items[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        items[items.length - 1].focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (idx >= 0) choose(items[idx]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      } else if (e.key === 'Tab') {
        close(false);
      }
    });

    document.addEventListener('click', (e) => {
      if (!wrap.contains(e.target) && !panel.hidden) close(false);
    });

    // keep the label in sync when scripts change the native select
    select.addEventListener('change', buildOptions);
  }

  function boot() {
    document.querySelectorAll('select[data-custom-select]').forEach(enhance);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // re-enhance after facets replace the collection toolbar
  const observer = new MutationObserver(() => boot());
  observer.observe(document.body, { childList: true, subtree: true });
})();
